/**
 * Latency / FPS climb gate — PRODUCT DECISION B.
 *
 * The shared congestion ladder stays deliberately difference-based: it reads queueing above
 * a per-peer RTT floor, not absolute RTT, so a distant-but-clean link is never mislabelled
 * as congested. That is correct for ENTERING recovery, but it has a hole at the EXIT: a
 * peer whose queue drains to zero may climb its bitrate even while the player is still
 * seeing unacceptable latency or a collapsed framerate.
 *
 * This gate closes that hole. When the player-visible latency is over the glass-to-glass
 * budget AND the excess above the link's own floor is what put it there (see
 * classifyLatencyBudget — `over-budget-fixable`), or decode FPS falls below
 * LATENCY_CLIMB_GATE_FPS, the peer's bitrate must not climb while the condition persists.
 * (The cut itself is owned by the ladder and the latency budget; this gate only parks the
 * climb.) Release is not instant: once locked, the gate only re-opens after
 * LATENCY_CLIMB_GATE_CLEAN_MS of continuously clean samples (no fixable excess, FPS at
 * target, and the ladder still healthy). A link that has proven itself faulty — most likely
 * a moving mobile connection — is held steady for a long while rather than fiddled with,
 * because consistent latency matters more to a player than peak quality.
 *
 * WHAT IS NOT FAULT. A link whose floor alone is over the budget is a guest far from the
 * host. Nothing the encoder does changes propagation, so the gate never locks on it: the
 * same excess reads the same at any distance, and a distant guest climbs back to full
 * quality exactly like a nearby one once their queue has drained. There is no absolute
 * network-latency input here at all — the candidate-pair RTT is a two-way, network-only
 * number and is not a fallback for a one-way glass-to-glass budget. Without a calibrated
 * reading and a trusted floor, the gate has nothing to say about latency and leaves the
 * queue to the ladder's own exit thresholds.
 *
 * Fail-safe direction: missing evidence never releases a lock (a locked gate stays locked),
 * and never locks a clean link (absence is not a bad reading). Only measured values count.
 */

import {
  LATENCY_BUDGET_GLASS_TO_GLASS_MS,
  LATENCY_BUDGET_MIN_FIXABLE_EXCESS_MS,
  classifyLatencyBudget,
} from './latencyBudgetContracts';
import { TARGET_FRAMERATE } from './webrtcLatencyContracts';

/**
 * Decode FPS below which the stream is unacceptable, regardless of bitrate — at the 60fps
 * reference rate. When a sample carries the rate actually delivered the threshold scales
 * with it (half of delivered), so a guest the host is only sending 30fps is not locked out
 * of its climb for decoding exactly 30.
 */
export const LATENCY_CLIMB_GATE_FPS = 30;

/**
 * How long samples must stay clean (no fixable excess AND FPS at target AND ladder healthy)
 * before a locked gate releases and the climb may resume. 30s of continuous clean streaming
 * is the price a link pays for having blown its SLO - a one-off blip recovers, a bad link
 * stays cut.
 */
export const LATENCY_CLIMB_GATE_CLEAN_MS = 30_000;

/** One sample of the quality evidence the gate is fed. Undefined = not measured. */
export type LatencyClimbGateSample = {
  /** Guest decode FPS (inbound-rtp framesPerSecond). */
  decodeFps?: number;
  /**
   * The framerate the encoder MEASURABLY put on the wire for this peer. Scales the FPS
   * threshold so a decoder keeping pace with a stepped-down stream is not read as collapsed.
   * Absent means the 60fps-referenced absolute threshold applies.
   *
   * Must be the delivered rate, not the configured one: gating the climb on the rate the
   * host intended to send means every host-side cut also locks the gate that would have let
   * the stream recover from it.
   */
  deliveredFps?: number;
  /** Calibrated glass-to-glass latency (datachannel handshake + report), ms. */
  calibratedLatencyMs?: number;
  /**
   * This link's calibrated latency floor (window minimum), ms. Required alongside
   * `calibratedLatencyMs` for the reading to count: without it the excess cannot be
   * attributed, and an over-budget reading is not evidence of fault.
   */
  calibratedFloorMs?: number;
};

export type LatencyClimbGateState = 'bad' | 'locked' | 'released' | 'hold';

export type LatencyClimbGateOptions = {
  budgetMs?: number;
  minFixableExcessMs?: number;
  fpsThreshold?: number;
  cleanHeldMs?: number;
  nowMs?: () => number;
};

export class LatencyClimbGate {
  private locked = false;
  private cleanSinceMs: number | null = null;
  private lastObservationMs: number | null = null;
  private readonly budgetMs: number;
  private readonly minFixableExcessMs: number;
  private readonly fpsThreshold: number;
  private readonly cleanHeldMs: number;
  private readonly nowMs: () => number;

  constructor(options: LatencyClimbGateOptions = {}) {
    this.budgetMs = options.budgetMs ?? LATENCY_BUDGET_GLASS_TO_GLASS_MS;
    this.minFixableExcessMs = options.minFixableExcessMs ?? LATENCY_BUDGET_MIN_FIXABLE_EXCESS_MS;
    this.fpsThreshold = options.fpsThreshold ?? LATENCY_CLIMB_GATE_FPS;
    this.cleanHeldMs = options.cleanHeldMs ?? LATENCY_CLIMB_GATE_CLEAN_MS;
    this.nowMs = options.nowMs ?? (() => Date.now());
  }

  isLocked(): boolean {
    return this.locked;
  }

  /** Losing measurement authority discards clean time without changing the lock. */
  invalidateEvidence(): void {
    this.cleanSinceMs = null;
    this.lastObservationMs = null;
  }

  /** The FPS floor for one sample: the configured threshold scaled to the rate delivered. */
  private fpsThresholdFor(deliveredFps: number | undefined): number {
    if (typeof deliveredFps !== 'number' || !Number.isFinite(deliveredFps) || deliveredFps <= 0) {
      return this.fpsThreshold;
    }
    return this.fpsThreshold * (deliveredFps / TARGET_FRAMERATE);
  }

  /**
   * Observe one sample alongside the ladder verdict.
   *
   * @param ladderHealthy True when the shared ladder says the link is healthy (verdict
   *                      'none'), so a lock cannot release against a still-queueing link.
   * @returns
   *   'bad'      — this sample violates the latency/FPS SLO; the gate is (or stays) locked.
   *   'locked'   — locked by an earlier sample, still waiting out the clean window.
   *   'released' — the clean window just completed; the climb may resume.
   *   'hold'     — nothing measured is bad and the gate is not locked.
   */
  observe(sample: LatencyClimbGateSample, ladderHealthy: boolean): LatencyClimbGateState {
    const now = this.nowMs();
    if (!Number.isFinite(now) || (this.lastObservationMs !== null
      && (now <= this.lastObservationMs || now - this.lastObservationMs >= 3_000))) {
      this.invalidateEvidence();
    }
    this.lastObservationMs = Number.isFinite(now) ? now : null;
    const latency = classifyLatencyBudget(
      { calibratedMs: sample.calibratedLatencyMs, floorMs: sample.calibratedFloorMs },
      this.budgetMs,
      this.minFixableExcessMs,
    );
    const bad =
      latency === 'over-budget-fixable' ||
      (Number.isFinite(sample.decodeFps) && sample.decodeFps! >= 0
        && sample.decodeFps! < this.fpsThresholdFor(sample.deliveredFps));
    if (bad) {
      this.locked = true;
      this.cleanSinceMs = null;
      return 'bad';
    }
    if (!this.locked) return 'hold';

    const hasEvidence = (Number.isFinite(sample.decodeFps) && sample.decodeFps! >= 0) || latency !== 'unmeasured';
    if (!ladderHealthy || !hasEvidence || !Number.isFinite(now)) {
      this.invalidateEvidence();
      return 'locked';
    }
    if (this.cleanSinceMs === null) this.cleanSinceMs = now;
    if (now - this.cleanSinceMs >= this.cleanHeldMs) {
      this.locked = false;
      this.cleanSinceMs = null;
      return 'released';
    }
    return 'locked';
  }
}
